// Image Picker Service - Pick from camera or gallery (mobile) or file input (web)

import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Camera, CameraResultType, CameraSource, type ImageOptions } from '@capacitor/camera';
import { ImageOptimizerService } from './image-optimizer.service';

export type ImagePickerSource = 'camera' | 'gallery' | 'prompt';

export type PickDocumentResult = { file: File } | { file: null; error?: 'too_large' | 'invalid_type' };

@Injectable({
  providedIn: 'root',
})
export class ImagePickerService {
  constructor(private imageOptimizer: ImageOptimizerService) {}

  /**
   * Check if we're running in a native Capacitor context (mobile).
   */
  get isNative(): boolean {
    return Capacitor.isNativePlatform();
  }

  /**
   * Pick a single image: on mobile offers camera or gallery; on web uses file input.
   * Returns an optimized File, or null if cancelled or error.
   */
  async pickImage(options?: {
    source?: ImagePickerSource;
    optimize?: boolean;
    maxDimension?: number;
    quality?: number;
  }): Promise<File | null> {
    const optimize = options?.optimize !== false;
    const source = options?.source ?? 'prompt';
    try {
      // Match new-request behavior on mobile: use file-input chooser for prompt flow.
      const file = (this.isNative && source !== 'prompt')
        ? await this.pickImageNative(source)
        : await this.pickImageWeb();

      if (!file) return null;
      return optimize ? this.imageOptimizer.optimize(file, {
        maxDimension: options?.maxDimension,
        quality: options?.quality,
      }) : file;
    } catch (err) {
      if (this.isUserCancelled(err)) {
        return null;
      }
      console.error('Image pick failed:', err);
      return null;
    }
  }

  /**
   * On native: show action sheet to choose camera or gallery, then get photo.
   */
  private async pickImageNative(source?: ImagePickerSource): Promise<File | null> {
    const preferredSource = source === 'camera'
      ? CameraSource.Camera
      : source === 'gallery'
        ? CameraSource.Photos
        : CameraSource.Prompt;

    const opts: ImageOptions = {
      quality: 90,
      allowEditing: false,
      resultType: CameraResultType.DataUrl,
      source: preferredSource,
    };

    let result;
    try {
      result = await Camera.getPhoto(opts);
    } catch (err) {
      // Prompt source can be flaky on some native devices; fallback to Photos.
      if (preferredSource !== CameraSource.Prompt) throw err;
      result = await Camera.getPhoto({ ...opts, source: CameraSource.Photos });
    }

    if (!result?.dataUrl) return null;
    return this.dataUrlToFile(result.dataUrl, result.format ?? 'jpeg');
  }

  private isUserCancelled(err: unknown): boolean {
    if (!err || typeof err !== 'object') return false;
    const maybeMessage = 'message' in err ? String(err.message ?? '').toLowerCase() : '';
    return maybeMessage.includes('cancel');
  }

  private dataUrlToFile(dataUrl: string, format: string): File {
    const mime = format === 'png' ? 'image/png' : 'image/jpeg';
    const base64 = dataUrl.replace(/^data:image\/\w+;base64,/, '');
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: mime });
    const ext = format === 'png' ? 'png' : 'jpg';
    return new File([blob], `image.${ext}`, { type: mime, lastModified: Date.now() });
  }

  /**
   * On web: open file input (accept images).
   */
  private pickImageWeb(): Promise<File | null> {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/jpeg,image/png,image/webp,image/heic';
      input.multiple = false;
      // Must be in the DOM for Safari / some browsers to open the dialog
      input.style.position = 'fixed';
      input.style.top = '-9999px';
      input.style.opacity = '0';
      document.body.appendChild(input);
      const cleanup = () => input.remove();
      input.onchange = () => {
        const file = input.files?.[0] ?? null;
        cleanup();
        resolve(file);
      };
      input.oncancel = () => { cleanup(); resolve(null); };
      input.click();
    });
  }

  /**
   * Pick a single document (PDF or image). Used for certificates, insurance, etc.
   * Accepts: PDF, JPEG, PNG. Optional max size in MB (default 10).
   * Works on web and in Capacitor WebView via file input.
   */
  async pickDocument(options?: {
    accept?: string;
    maxSizeMb?: number;
  }): Promise<PickDocumentResult> {
    const accept = options?.accept ?? 'application/pdf,image/jpeg,image/png';
    const maxSizeMb = options?.maxSizeMb ?? 10;
    const maxBytes = maxSizeMb * 1024 * 1024;
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];

    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = accept;
      input.multiple = false;
      // Must be in the DOM for Safari / some browsers to open the dialog
      input.style.position = 'fixed';
      input.style.top = '-9999px';
      input.style.opacity = '0';
      document.body.appendChild(input);
      const cleanup = () => input.remove();
      input.onchange = () => {
        const file = input.files?.[0] ?? null;
        if (!file) {
          cleanup();
          resolve({ file: null });
          return;
        }
        if (file.size > maxBytes) {
          cleanup();
          resolve({ file: null, error: 'too_large' });
          return;
        }
        if (!allowedTypes.includes(file.type)) {
          cleanup();
          resolve({ file: null, error: 'invalid_type' });
          return;
        }
        cleanup();
        resolve({ file });
      };
      input.oncancel = () => { cleanup(); resolve({ file: null }); };
      input.click();
    });
  }

  /**
   * Show a simple action sheet (for web we only have file picker; for native we can offer camera vs gallery).
   * Returns chosen source so the caller can call pickImage({ source }).
   */
  async chooseSource(): Promise<ImagePickerSource | null> {
    if (!this.isNative) return 'gallery'; // web: only file picker
    // On native, CameraSource.Prompt is used in pickImage(); no separate sheet needed unless we want custom UI
    return null; // use default Prompt
  }
}
